import { findLastIndex, sortBy } from "lodash";
import { format, parseISO, subMinutes } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import { FIRST_EEG_SERVICE, TIMEZONE } from "../../../../utils/constants";
import { PriorityBlock, PrioritySession, RelativePosition } from "./types.nfbAssign";

const ACTIVATION_LEAD_MINUTES = 25;
const EVENING_ACTIVATION_LEAD_MINUTES = 25; // kept at 25 for now; lower it to relax evening blocks again
const EVENING_START_HOUR = 17;

const getActivationLeadMinutes = (now: Date) => {
	// This FN is usefull for when we will have session at both 17:30 and 17:45, when schedules changes
	const hourInPortugal = utcToZonedTime(now, TIMEZONE).getHours();
	const leadMinutes =
		hourInPortugal >= EVENING_START_HOUR ? EVENING_ACTIVATION_LEAD_MINUTES : ACTIVATION_LEAD_MINUTES;
	return leadMinutes;
};

// first evaluations rank after everyone else; then checked-in earlier ranks first;
// not checked in ranks last, by creation order
const sortByPriority = (sessions: Array<PrioritySession>) => {
	const isFirstEvaluation = (session: PrioritySession) => session.service?.id === FIRST_EEG_SERVICE;
	const checkinTime = (session: PrioritySession) =>
		session.checkedin_at ? parseISO(session.checkedin_at).getTime() : Infinity;
	const creationDate = (session: PrioritySession) => session.date_created ?? "";

	const sorted = sortBy(sessions, [isFirstEvaluation, checkinTime, creationDate]);
	return sorted;
};

const resolveRelativePosition = (index: number, currentIndex: number): RelativePosition => {
	if (index === currentIndex) return "current";
	if (index < currentIndex) return "previous";
	return "next";
};

const groupSessionsBySlot = (sessions: Array<PrioritySession>) => {
	const groups = new Map<string, Array<PrioritySession>>();
	sessions.forEach((session) => {
		const slotLabel = format(parseISO(session.start_date), "HH:mm");
		const slotSessions = groups.get(slotLabel) ?? [];
		slotSessions.push(session);
		groups.set(slotLabel, slotSessions);
	});
	return groups;
};

export const buildWidgetSessionBlocks = (sessions: Array<PrioritySession>, now: Date) => {
	const groups = groupSessionsBySlot(sessions);

	const sortedBlocks = Array.from(groups.entries())
		.map(([slotLabel, slotSessions]) => ({ slotLabel, sessions: sortByPriority(slotSessions) }))
		.sort((a, b) => a.slotLabel.localeCompare(b.slotLabel));

	// a block activates a few minutes before its start; the current one is the latest already activated
	const isActivated = (block: { sessions: Array<PrioritySession> }) => {
		const activationTime = subMinutes(parseISO(block.sessions[0].start_date), getActivationLeadMinutes(now));
		const activated = activationTime.getTime() <= now.getTime();
		return activated;
	};
	const currentIndex = findLastIndex(sortedBlocks, isActivated);

	const blocks: Array<PriorityBlock> = sortedBlocks.map((block, index) => ({
		...block,
		relative: resolveRelativePosition(index, currentIndex)
	}));

	// Show only the current block plus the one before and the one after it.
	const anchorIndex = currentIndex === -1 ? 0 : currentIndex;
	const windowedBlocks = blocks.slice(Math.max(0, anchorIndex - 1), anchorIndex + 2);

	return windowedBlocks;
};
